import React, { useEffect, useState } from "react";
import { Cookie, X } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Slim, single-line cookie / preferences banner shown once per browser.
 *
 * DPDP Act 2023 hygiene:
 *  - Uses strictly-necessary cookies only (no advertising / tracking).
 *  - Explicit link to the Privacy Policy PDF so users can review before dismissing.
 *  - "Got it" writes a timestamp to localStorage so the banner stays gone.
 *
 * We deliberately keep this a *notice* (not a consent gate) because we only use
 * strictly-necessary cookies which do not require prior consent under the DPDP
 * Act. If we ever add analytics / marketing cookies, this component must be
 * upgraded to a proper consent manager with granular opt-in.
 */
const STORAGE_KEY = "hn_cookie_notice_dismissed_at";

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      if (!localStorage.getItem(STORAGE_KEY)) setVisible(true);
    } catch {
      // Storage disabled (private mode / cookies blocked) — show once per tab.
      setVisible(true);
    }
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, new Date().toISOString()); }
    catch { /* ignore */ }
    setVisible(false);
  };

  const privacyUrl = `${process.env.REACT_APP_BACKEND_URL}/api/legal/privacy.pdf`;

  return (
    <div
      data-testid="cookie-banner"
      role="region"
      aria-label="Cookie notice"
      className="fixed bottom-3 left-1/2 -translate-x-1/2 z-[55] w-[calc(100%-1.5rem)] sm:w-auto sm:max-w-2xl px-4 py-2.5 rounded-full bg-stone-900/95 text-white shadow-2xl border border-stone-700 backdrop-blur flex items-center gap-3"
    >
      <Cookie size={16} className="text-marigold-300 flex-shrink-0" aria-hidden="true" />
      <div className="text-xs sm:text-sm leading-snug flex-1 min-w-0">
        <span className="sm:whitespace-nowrap">We use strictly-necessary cookies to keep you signed in and process bookings.</span>{" "}
        <a
          href={privacyUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="cookie-banner-privacy-link"
          className="underline decoration-marigold-400 underline-offset-2 hover:text-marigold-200 font-semibold whitespace-nowrap"
        >
          Read our Privacy Policy
        </a>
      </div>
      <button
        type="button"
        onClick={dismiss}
        data-testid="cookie-banner-dismiss"
        className="inline-flex items-center gap-1 rounded-full bg-white text-stone-900 hover:bg-marigold-100 h-8 px-3 text-xs font-black uppercase tracking-wider active:scale-95 transition-transform"
      >
        Got it
      </button>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss cookie notice"
        className="text-white/60 hover:text-white sm:hidden"
      >
        <X size={16} />
      </button>
    </div>
  );
}
